import { inject, Injectable } from '@angular/core';
import { combineLatest } from 'rxjs';
import { Point2D } from '../../../../team-apis/otterdam/answers/shape-typings';
import { FreePointMeasurement } from '../../../state/render-state';
import { MouseCoordinateService } from '../../../utils/mouse-coordinate.service';
import { GlobalRenderTriggerService } from '../../global-render-trigger.service';
import { AbstractMeshService } from '../abstract-mesh.service';
import { MeasurementLineHelper } from './measurement-line-helper';
import { MeasurementPoleHelper } from './measurement-pole-helper';

enum HelperIndex {
  Reference,
  Target,
  Line,
}

@Injectable()
export class FreePointMeasurementService extends AbstractMeshService<
  MeasurementPoleHelper | MeasurementLineHelper
> {
  private globalRenderTriggerService = inject(GlobalRenderTriggerService);
  private mouseCoordinateService = inject(MouseCoordinateService);

  constructor() {
    super();
    this.setUpSubscriptions();
  }

  private setUpSubscriptions() {
    this.subscribeTo(
      combineLatest([
        this.store('render')('freePointMeasurement').$,
        this.store('mouse')('containerPoint').$,
      ]),
      ([measurement, containerPoint]) => {
        if (measurement) {
          this.addHelpers();

          if (measurement.active) {
            this.updatePolePositions(measurement, containerPoint);
            this.updateLine();
            this.globalRenderTriggerService.trigger$.next();
          }
        } else {
          this.removeHelpers();
        }
      },
    );
  }

  private addHelpers() {
    if (this.helpers.size !== 0) {
      return;
    }

    const referencePoleHelper = new MeasurementPoleHelper('reference');
    const targetPoleHelper = new MeasurementPoleHelper('target');
    const distanceLine = new MeasurementLineHelper();

    this.addHelper(HelperIndex.Reference, referencePoleHelper);
    this.addHelper(HelperIndex.Target, targetPoleHelper);
    this.addHelper(HelperIndex.Line, distanceLine);
  }

  private removeHelpers() {
    this.removeHelper(HelperIndex.Reference);
    this.removeHelper(HelperIndex.Target);
    this.removeHelper(HelperIndex.Line);
  }

  private updateLine() {
    const { reference, target } = this.getPoleHelpers();
    const line = this.getHelper<MeasurementLineHelper>(HelperIndex.Line);

    line.update(reference.getPosition(), target.getPosition());
  }

  private updatePolePositions(
    measurement: FreePointMeasurement,
    containerPoint: Point2D,
  ) {
    const { reference, target } = this.getPoleHelpers();

    reference.updatePosition(measurement.reference);
    target.updatePosition(
      this.mouseCoordinateService.getGroundedMousePosition(containerPoint),
    );
  }

  private getPoleHelpers() {
    return {
      reference: this.getHelper<MeasurementPoleHelper>(HelperIndex.Reference),
      target: this.getHelper<MeasurementPoleHelper>(HelperIndex.Target),
    };
  }
}
